import { IcuDiaryItem } from '@/api'
import { Loader } from '@/components'
import { useCurrentPatient, useIcuDiary } from '@/services'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import { useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

dayjs.locale('de')

const ArchiveEntry = ({ entry }: { entry: IcuDiaryItem }) => {
  const date = useMemo(() => dayjs(entry.creation_time), [entry.creation_time])
  const isProvider = entry.author_type === 'hospital_worker'

  return (
    <div className="mb-8 break-inside-avoid">
      {/* Author & date line */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-b0 ${
              isProvider ? 'bg-accent' : 'bg-[#E8836B]'
            }`}
          >
            {isProvider ? (
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M13 10H12V9C12 8.45 11.55 8 11 8C10.45 8 10 8.45 10 9V10H9C8.45 10 8 10.45 8 11C8 11.55 8.45 12 9 12H10V13C10 13.55 10.45 14 11 14C11.55 14 12 13.55 12 13V12H13C13.55 12 14 11.55 14 11C14 10.45 13.55 10 13 10Z"
                  fill="white"
                />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 14.4L7.1 13.58C3.72 10.52 1.6 8.58 1.6 6.2C1.6 4.26 3.14 2.72 5.08 2.72C6.18 2.72 7.24 3.24 8 4.06C8.76 3.24 9.82 2.72 10.92 2.72C12.86 2.72 14.4 4.26 14.4 6.2C14.4 8.58 12.28 10.52 8.9 13.58L8 14.4Z"
                  fill="white"
                />
              </svg>
            )}
          </div>
          <div>
            <div className="text-[13px] font-semibold text-b100">
              {entry.author_name || (isProvider ? 'Behandelnde:r' : 'Angehörige:r')}
            </div>
            {entry.author_role && <div className="text-[11px] text-b50">{entry.author_role}</div>}
          </div>
        </div>
        <div className="text-[12px] text-b50">{date.format('HH:mm')} Uhr</div>
      </div>

      {/* Image */}
      {entry.image && (
        <img
          src={entry.image}
          alt=""
          className="w-full rounded-xl object-cover max-h-[240px] mb-3"
        />
      )}

      {/* Text */}
      <p className="text-textS text-b90 leading-[1.8] whitespace-pre-wrap">{entry.text}</p>

      {/* Separator */}
      <div className="mt-6 flex justify-center">
        <div className="flex items-center gap-2">
          <div className="w-1 h-1 rounded-full bg-sec30" />
          <div className="w-1.5 h-1.5 rounded-full bg-sec50" />
          <div className="w-1 h-1 rounded-full bg-sec30" />
        </div>
      </div>
    </div>
  )
}

const DateJumpPill = ({
  date,
  isActive,
  onClick,
}: {
  date: string
  isActive: boolean
  onClick: () => void
}) => (
  <button
    onClick={onClick}
    className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-medium cursor-pointer border transition-all duration-200 ${
      isActive ? 'bg-primary text-b0 border-primary' : 'bg-b0 text-b50 border-sec10 active:bg-sec10'
    }`}
  >
    {dayjs(date).format('DD. MMM')}
  </button>
)

export const DiaryArchive = () => {
  const { patientId } = useParams<{ patientId: string }>()
  const { data: patient } = useCurrentPatient()
  const diaryResponse = useIcuDiary(patientId!)
  const { data = [], isFetched } = diaryResponse

  const [activeDate, setActiveDate] = useState<string | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const dateRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Group entries by date, sorted chronologically
  const groupedEntries = useMemo(() => {
    const groups: { date: string; entries: IcuDiaryItem[] }[] = []
    let currentDate = ''

    const sorted = [...data].sort(
      (a, b) => new Date(a.creation_time).getTime() - new Date(b.creation_time).getTime(),
    )

    sorted.forEach((entry) => {
      const entryDate = dayjs(entry.creation_time).format('YYYY-MM-DD')
      if (entryDate !== currentDate) {
        currentDate = entryDate
        groups.push({ date: entryDate, entries: [entry] })
      } else {
        groups[groups.length - 1].entries.push(entry)
      }
    })

    return groups
  }, [data])

  const uniqueDates = useMemo(() => groupedEntries.map((g) => g.date), [groupedEntries])

  const scrollToDate = (date: string) => {
    setActiveDate(date)
    dateRefs.current[date]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handlePdfDownload = () => {
    // Future: generate PDF from diary content
    window.print()
  }

  if (!isFetched) return <Loader />

  // Calculate stay duration
  const firstEntry = data.length > 0 ? dayjs(data[data.length - 1]?.creation_time) : null
  const lastEntry = data.length > 0 ? dayjs(data[0]?.creation_time) : null
  const stayDuration = firstEntry && lastEntry ? lastEntry.diff(firstEntry, 'day') + 1 : 0

  return (
    <div className="flex flex-col h-full w-full max-w-[488px] mx-auto">
      {/* Archive header */}
      <div
        className="px-5 pt-6 pb-4 shrink-0"
        style={{
          background: 'linear-gradient(180deg, #F0F9F7 0%, #FAFEFE 100%)',
        }}
      >
        {/* Book-like header */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent/10 text-[11px] font-semibold text-accent uppercase tracking-wider mb-3">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 2C3 1.45 3.45 1 4 1H10L13 4V14C13 14.55 12.55 15 12 15H4C3.45 15 3 14.55 3 14V2Z"
                stroke="currentColor"
                strokeWidth="1.2"
              />
            </svg>
            Tagebuch-Archiv
          </div>
          <h1 className="text-h2 text-sec100 mb-1">
            {patient?.first_name} {patient?.last_name}
          </h1>
          <p className="text-textS text-b50">
            {patient?.station} · {patient?.hospital}
          </p>
        </div>

        {/* Stats */}
        <div className="flex justify-center gap-6 mb-4">
          <div className="text-center">
            <div className="text-h3 text-primary">{data.length}</div>
            <div className="text-[11px] text-b50">Einträge</div>
          </div>
          <div className="w-[1px] bg-sec10" />
          <div className="text-center">
            <div className="text-h3 text-primary">{stayDuration}</div>
            <div className="text-[11px] text-b50">Tage</div>
          </div>
          <div className="w-[1px] bg-sec10" />
          <div className="text-center">
            <div className="text-h3 text-primary">{uniqueDates.length}</div>
            <div className="text-[11px] text-b50">Daten</div>
          </div>
        </div>

        {/* PDF download button */}
        <button
          onClick={handlePdfDownload}
          className="w-full flex items-center justify-center gap-2 h-11 rounded-xl bg-b0 border border-sec10 text-[13px] font-semibold text-sec100 cursor-pointer transition-all duration-200 active:bg-sec10 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d="M9 3V12M9 12L5.5 8.5M9 12L12.5 8.5"
              stroke="#2D647D"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M3 13V14C3 14.55 3.45 15 4 15H14C14.55 15 15 14.55 15 14V13"
              stroke="#2D647D"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          Als PDF herunterladen
        </button>
      </div>

      {/* Date navigation pills */}
      {uniqueDates.length > 1 && (
        <div className="px-4 py-2 flex gap-2 overflow-x-auto no-scrollbar shrink-0 bg-b0 border-b border-sec10">
          {uniqueDates.map((date) => (
            <DateJumpPill
              key={date}
              date={date}
              isActive={activeDate === date}
              onClick={() => scrollToDate(date)}
            />
          ))}
        </div>
      )}

      {/* Archive content - book-like */}
      <div
        ref={contentRef}
        className="flex-1 overflow-y-auto"
        style={{
          background: 'linear-gradient(180deg, #FFFCF8 0%, #FFFFFF 50%, #FFFCF8 100%)',
        }}
      >
        {/* Gentle introduction text */}
        <div className="px-6 pt-6 pb-4 text-center">
          <p className="text-textS text-b50 italic leading-relaxed">
            Dieses Tagebuch dokumentiert den Aufenthalt
            {patient?.first_name ? ` von ${patient.first_name}` : ''} — geschrieben von Angehörigen
            und dem Behandlungsteam.
          </p>
          <div className="flex justify-center mt-3">
            <div className="w-12 h-[1.5px] bg-gradient-to-r from-transparent via-sec30 to-transparent" />
          </div>
        </div>

        {/* Entries grouped by date */}
        <div className="px-6 pb-10">
          {groupedEntries.map((group) => (
            <div
              key={group.date}
              ref={(el) => {
                dateRefs.current[group.date] = el
              }}
            >
              {/* Date heading */}
              <div className="flex items-center gap-3 mb-5 mt-2">
                <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-sec10" />
                <div className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-sec10">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <rect
                      x="2"
                      y="3"
                      width="12"
                      height="11"
                      rx="2"
                      stroke="#8AAFB7"
                      strokeWidth="1"
                    />
                    <path d="M2 7H14" stroke="#8AAFB7" strokeWidth="1" />
                    <path
                      d="M5 1V4M11 1V4"
                      stroke="#8AAFB7"
                      strokeWidth="1"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span className="text-[12px] font-semibold text-sec100">
                    {dayjs(group.date).format('dddd, DD. MMMM YYYY')}
                  </span>
                </div>
                <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-sec10" />
              </div>

              {/* Entries for this date */}
              {group.entries.map((entry) => (
                <ArchiveEntry key={entry.id} entry={entry} />
              ))}
            </div>
          ))}
        </div>

        {/* End of diary */}
        <div className="px-6 pb-10 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-[1.5px] bg-gradient-to-r from-transparent via-sec30 to-transparent" />
          </div>
          <p className="text-[13px] text-b50 italic">Ende des Tagebuchs</p>
          {patient?.deactivated_at && (
            <p className="text-[11px] text-b30 mt-1">
              Entlassung am {dayjs(patient.deactivated_at).format('DD. MMMM YYYY')}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
